import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { merchantsService } from '@/services/merchants';
import { CitiesService } from '@/services/cities';
import courtReplayLogo from '@/assets/favicon.ico'
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

interface Brand {
  id: number;
  name: string;
  logo_url: string;
  city: string;
  province: string;
  address: string;
}

const Brands = () => {
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [uniqueCities, setUniqueCities] = useState<object[]>([]);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 6;

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true); // ⬅️ start loading

        const response = await merchantsService.fetchMerchants({
          limit: limit.toString(),
          page: currentPage.toString(),
          search: searchQuery.length > 3 ? searchQuery : "",
          city_id: selectedCity !== 'all' ? parseInt(selectedCity) : undefined,
        });

        const formattedBrands = response.merchants?.map((merchant) => ({
          id: parseInt(merchant.id),
          logo: merchant.logo,
          name: merchant.name,
          logo_url: "",
          city: merchant.city,
          province: merchant.province,
          address: merchant.address,
        }));

        setBrands(formattedBrands);
        setTotalCount(response.pagination.total);
      } catch (error) {
        console.error("Failed to fetch brands:", error);
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 300);
      }
    };

    fetchBrands();
  }, [currentPage, searchQuery.length > 3, selectedCity]);


  const handleBrandSelect = (brandName: string, brandId: number) => {
    const slug = brandName.toLowerCase().replace(/\s+/g, "_");
    navigate(`/brands/${slug}-${brandId}/courts`);
  };


  // Get unique cities for filter dropdown
  // const uniqueCities = Array.from(new Set(brands.map(brand => brand.city)));
  async function getUniqueCities() {
    try {
      const data = await CitiesService.fetchCities();
      const uniqueCities = data.data;
      console.log(uniqueCities); // now an array
      return uniqueCities;
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      return [];
    }
  }

  useEffect(() => {
    getUniqueCities().then(cities => {
      setUniqueCities(cities);
    });
  }, []);

  const BrandSkeleton = () => {
    return (
      <Card className="rounded-2xl border bg-white/80 backdrop-blur">
        <CardContent className="p-6 text-center space-y-4">
          <Skeleton circle height={96} width={96} className="mx-auto" />
          <Skeleton height={20} width="70%" className="mx-auto" />
          <Skeleton height={15} width="50%" className="mx-auto" />
          <Skeleton height={12} count={2} />
          <Skeleton height={36} />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-white via-white to-green-100">
      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-2">
        <div className="mb-10 text-center">
          <h2 className="text-4xl font-extrabold tracking-tight mb-3">
            Choose a Court Brand
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Select the court where you played and instantly find your video replays
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-10 bg-white/70 backdrop-blur rounded-xl p-6 shadow-sm border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search brand or city..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                className="pl-10"
              />
            </div>

            {/* Filter */}
            <Select value={selectedCity} onValueChange={(e) => { setSelectedCity(e); setCurrentPage(1) }}>
              <SelectTrigger>
                <SelectValue placeholder="All cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All cities</SelectItem>
                {uniqueCities?.map((city) => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading
            ? Array.from({ length: limit }).map((_, i) => (
                <BrandSkeleton key={i} />
              ))
            : brands.map((brand) => (
                <Card
                  key={brand.id}
                  onClick={() => handleBrandSelect(brand.name, brand.id)}
                  className="
                    cursor-pointer 
                    group
                    rounded-2xl 
                    border 
                    bg-white/80 
                    backdrop-blur 
                    transition-all 
                    hover:-translate-y-1 
                    hover:shadow-xl
                  "
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-24 h-24 mx-auto mb-4 rounded-xl bg-white shadow flex items-center justify-center overflow-hidden">
                      <img
                        src={brand.logo || courtReplayLogo}
                        alt={brand.name}
                        className="object-contain w-full h-full"
                      />
                    </div>

                    <h3 className="text-xl font-semibold mb-1">
                      {brand.name}
                    </h3>

                    <p className="text-sm text-muted-foreground">
                      {brand.city}, {brand.province}
                    </p>

                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {brand.address}
                    </p>

                    <Button className="w-full mt-5 group-hover:bg-primary/90">
                      View Courts
                    </Button>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Pagination Controls */}
        {totalCount > limit && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {Math.ceil(totalCount / limit)}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={currentPage >= Math.ceil(totalCount / limit)}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-12 bg-white rounded-lg p-6 mb-5">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/library')}
            >
              My Library
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/cart')}
            >
              View Cart
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/profile')}
            >
              Profile Settings
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Brands;